import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { useRef, useEffect, useState } from 'react';
import { LogoC } from '../components/brand/LogoMarks';
import {
  Upload, Sparkles, Share2, CheckCircle2,
  ArrowRight, Play, Scissors, Menu, X,
} from 'lucide-react';

/* ─── Smooth scroll helper ─────────────────────────────────────────────── */
const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth' });
};

/* ─── Animation presets ────────────────────────────────────────────────── */
const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 44 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.85, ease: [0.16, 1, 0.3, 1], delay },
});

/* ─── Section Label ────────────────────────────────────────────────────── */
const SectionLabel = ({ children }) => (
  <p
    className="text-xs font-semibold uppercase tracking-[0.22em] mb-5"
    style={{ color: '#1DDFCA' }}
  >
    {children}
  </p>
);

/* ─── Divider ──────────────────────────────────────────────────────────── */
const Divider = () => (
  <div className="max-w-6xl mx-auto px-6">
    <div
      style={{
        height: 1,
        background:
          'linear-gradient(90deg, transparent, rgba(255,255,255,0.06) 30%, rgba(255,255,255,0.06) 70%, transparent)',
      }}
    />
  </div>
);

/* ─── Animated Counter ─────────────────────────────────────────────────── */
function AnimatedCounter({ value, suffix = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const num = parseFloat(value);
    if (isNaN(num)) { setDisplay(value); return; }
    const start = performance.now();
    const duration = 1800;
    const tick = (ts) => {
      const p = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(eased * num));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, value]);

  return (
    <span ref={ref}>
      {display}
      {suffix}
    </span>
  );
}

/* ─── App UI Mockup ────────────────────────────────────────────────────── */
function AppMockup() {
  const navItems = ['Dashboard', 'Drive', 'Create', 'Publish', 'Settings'];
  const videos = [
    { name: 'interview_marathon.mp4', dur: '24:15', clips: '5 clips', active: true },
    { name: 'podcast_ep12.mp4', dur: '1:12:08', clips: null, active: false },
    { name: 'product_demo.mp4', dur: '8:44', clips: null, active: false },
    { name: 'vlog_nyc_final.mp4', dur: '18:22', clips: null, active: false },
    { name: 'tutorial_v2.mp4', dur: '32:05', clips: null, active: false },
    { name: 'raw_footage_b.mp4', dur: '45:18', clips: null, active: false },
  ];

  return (
    <div
      className="rounded-2xl overflow-hidden w-full"
      style={{ background: '#101520', border: '1px solid rgba(255,255,255,0.09)' }}
    >
      {/* Window chrome */}
      <div
        className="flex items-center gap-1.5 px-4 h-9"
        style={{ background: '#0A0D15', borderBottom: '1px solid rgba(255,255,255,0.05)' }}
      >
        <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'rgba(255,95,87,0.65)' }} />
        <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'rgba(254,188,46,0.65)' }} />
        <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'rgba(40,200,64,0.65)' }} />
        <div className="flex-1 flex justify-center">
          <div className="h-3.5 w-44 rounded-full" style={{ background: '#151C2A' }} />
        </div>
      </div>
      {/* Body */}
      <div className="flex" style={{ height: 340 }}>
        {/* Sidebar */}
        <div
          className="w-40 flex-shrink-0 p-3"
          style={{ borderRight: '1px solid rgba(255,255,255,0.05)' }}
        >
          <div className="flex items-center gap-2 px-2 py-2 mb-3">
            <div className="w-5 h-5 rounded-lg flex-shrink-0" style={{ background: '#1DDFCA' }} />
            <div className="h-2.5 w-8 rounded" style={{ background: '#1A2238' }} />
          </div>
          {navItems.map((item, i) => (
            <div
              key={item}
              className="flex items-center gap-2 px-2 py-1.5 rounded-md mb-0.5"
              style={{ background: i === 1 ? 'rgba(46,121,255,0.1)' : 'transparent' }}
            >
              <div
                className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                style={{ background: i === 1 ? '#1DDFCA' : '#0E0E1C' }}
              />
              <span
                style={{
                  fontSize: 10,
                  color: i === 1 ? '#7EB8FF' : '#3D5272',
                  fontWeight: i === 1 ? 600 : 400,
                }}
              >
                {item}
              </span>
            </div>
          ))}
          <div className="mt-4 px-1">
            <div
              className="mb-1.5"
              style={{ fontSize: 8, color: '#181826', fontWeight: 600, letterSpacing: '0.1em' }}
            >
              FOLDERS
            </div>
            {['YouTube Content', 'Talking Camera', 'Reels', 'Published'].map((f, i) => (
              <div key={f} className="flex items-center gap-1.5 py-0.5">
                <div
                  className="w-2 h-2 rounded-sm flex-shrink-0"
                  style={{ background: i === 0 ? '#1DDFCA' : '#0E0E1C' }}
                />
                <span
                  style={{ fontSize: 9, color: i === 0 ? '#7EB8FF' : '#3D5272' }}
                  className="truncate"
                >
                  {f}
                </span>
                <span
                  className="ml-auto flex-shrink-0"
                  style={{ fontSize: 9, color: '#181826' }}
                >
                  {[6, 3, 8, 2][i]}
                </span>
              </div>
            ))}
          </div>
        </div>
        {/* Main */}
        <div className="flex-1 p-4 overflow-hidden">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-2.5 w-24 rounded" style={{ background: '#1A2438' }} />
            <div className="flex-1" />
            <div className="h-5 w-16 rounded" style={{ background: '#151C2A' }} />
            <div className="h-5 w-16 rounded" style={{ background: '#1DDFCA', opacity: 0.85 }} />
          </div>
          <div className="grid grid-cols-3 gap-2">
            {videos.map((v, i) => (
              <div
                key={i}
                className="rounded-lg overflow-hidden"
                style={{
                  border: `1px solid ${v.active ? 'rgba(46,121,255,0.5)' : 'rgba(255,255,255,0.05)'}`,
                  boxShadow: v.active ? '0 0 16px rgba(46,121,255,0.2)' : 'none',
                }}
              >
                <div
                  className="relative"
                  style={{ height: 62, background: `hsl(220,18%,${10 + i * 1.5}%)` }}
                >
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center"
                      style={{ background: 'rgba(255,255,255,0.07)' }}
                    >
                      <div
                        style={{
                          width: 0,
                          height: 0,
                          borderTop: '4px solid transparent',
                          borderBottom: '4px solid transparent',
                          borderLeft: '6px solid rgba(255,255,255,0.45)',
                          marginLeft: 2,
                        }}
                      />
                    </div>
                  </div>
                  <span
                    className="absolute bottom-1 right-1.5"
                    style={{
                      fontSize: 8,
                      color: 'rgba(255,255,255,0.55)',
                      background: 'rgba(0,0,0,0.65)',
                      padding: '1px 3px',
                      borderRadius: 3,
                    }}
                  >
                    {v.dur}
                  </span>
                  {v.clips && (
                    <span
                      className="absolute top-1 left-1"
                      style={{
                        fontSize: 8,
                        color: '#fff',
                        background: '#1DDFCA',
                        padding: '1px 5px',
                        borderRadius: 4,
                      }}
                    >
                      {v.clips}
                    </span>
                  )}
                </div>
                <div className="px-1.5 py-1">
                  <p style={{ fontSize: 8, color: '#3D5272' }} className="truncate">
                    {v.name}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Waveform Mockup ──────────────────────────────────────────────────── */
function WaveformMockup() {
  const bars = Array.from({ length: 60 }, (_, i) => {
    const h =
      18 +
      Math.abs(
        Math.sin(i * 0.7) * 55 + Math.sin(i * 1.9) * 28 + Math.sin(i * 3.1) * 12
      );
    const inClip =
      (i >= 10 && i <= 19) || (i >= 30 && i <= 40) || (i >= 49 && i <= 56);
    return { h: Math.min(h, 92), inClip };
  });

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{ background: '#101520', border: '1px solid rgba(255,255,255,0.08)' }}
    >
      <div
        className="px-5 py-4 flex items-center gap-3"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}
      >
        <div
          className="w-2 h-2 rounded-full"
          style={{ background: '#1DDFCA', boxShadow: '0 0 10px #1DDFCA' }}
        />
        <span style={{ fontSize: 11, color: '#8E9AB0', fontWeight: 600 }}>
          AI ANALYSIS — interview_marathon.mp4
        </span>
        <span
          className="ml-auto px-2.5 py-0.5 rounded-full"
          style={{
            fontSize: 10,
            background: 'rgba(46,121,255,0.1)',
            color: '#7EB8FF',
            fontWeight: 600,
          }}
        >
          3 moments detected
        </span>
      </div>
      <div className="px-5 pt-6 pb-5">
        <div className="flex items-end gap-px" style={{ height: 80 }}>
          {bars.map((bar, i) => (
            <motion.div
              key={i}
              className="flex-1"
              initial={{ scaleY: 0 }}
              whileInView={{ scaleY: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: i * 0.008, ease: [0.16, 1, 0.3, 1] }}
              style={{
                height: `${bar.h}%`,
                minHeight: 3,
                borderRadius: 2,
                background: bar.inClip
                  ? 'linear-gradient(to top, #1DDFCA, #B57EFF)'
                  : 'rgba(255,255,255,0.07)',
                transformOrigin: 'bottom',
                boxShadow: bar.inClip ? '0 0 6px rgba(46,121,255,0.4)' : 'none',
              }}
            />
          ))}
        </div>
        <div className="flex gap-2 mt-4">
          {[
            { label: 'Hook moment', time: '0:42', score: 94 },
            { label: 'Key insight', time: '3:17', score: 87 },
            { label: 'Story peak', time: '7:28', score: 91 },
          ].map((c, i) => (
            <div
              key={i}
              className="flex-1 flex items-center gap-2 px-3 py-2.5 rounded-xl"
              style={{
                background: 'rgba(46,121,255,0.07)',
                border: '1px solid rgba(46,121,255,0.14)',
              }}
            >
              <div
                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ background: '#1DDFCA' }}
              />
              <div className="flex-1 min-w-0">
                <p
                  style={{ fontSize: 9, color: '#7EB8FF', fontWeight: 600 }}
                  className="truncate"
                >
                  {c.label}
                </p>
                <p style={{ fontSize: 8, color: '#3D5272' }}>{c.time}</p>
              </div>
              <span style={{ fontSize: 11, color: '#7EB8FF', fontWeight: 700 }}>{c.score}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Hero Mockup (Create / Smart Detect view) ─────────────────────────── */
function HeroMockup() {
  const miniBars = Array.from({ length: 52 }, (_, i) => {
    const h =
      20 +
      Math.abs(
        Math.sin(i * 0.7) * 48 + Math.sin(i * 1.9) * 24 + Math.sin(i * 3.2) * 10
      );
    const inClip =
      (i >= 7 && i <= 14) || (i >= 22 && i <= 31) || (i >= 40 && i <= 47);
    return { h: Math.min(h, 90), inClip };
  });

  const clips = [
    {
      label: 'Hook moment',
      time: '0:42 – 1:10',
      score: 94,
      signals: ['Strong hook', 'Emotional peak'],
      active: true,
    },
    {
      label: 'Key insight',
      time: '3:17 – 4:02',
      score: 87,
      signals: ['Speech emphasis'],
      active: false,
    },
    {
      label: 'Story peak',
      time: '7:28 – 8:44',
      score: 91,
      signals: ['Engagement trigger'],
      active: false,
    },
  ];

  return (
    <div
      className="rounded-2xl overflow-hidden w-full"
      style={{ background: '#0C1220', border: '1px solid rgba(255,255,255,0.09)' }}
    >
      {/* Window chrome */}
      <div
        className="flex items-center gap-1.5 px-4 h-9"
        style={{ background: '#070A12', borderBottom: '1px solid rgba(255,255,255,0.05)' }}
      >
        <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'rgba(255,95,87,0.65)' }} />
        <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'rgba(254,188,46,0.65)' }} />
        <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'rgba(40,200,64,0.65)' }} />
        <div className="flex-1 flex justify-center">
          <div
            className="h-3.5 w-44 rounded-full flex items-center gap-2 px-2.5"
            style={{ background: '#111A2A' }}
          >
            <div
              className="w-1.5 h-1.5 rounded-full flex-shrink-0"
              style={{ background: 'rgba(46,121,255,0.45)' }}
            />
            <div
              className="h-1.5 flex-1 rounded-full"
              style={{ background: 'rgba(255,255,255,0.06)' }}
            />
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex" style={{ height: 400 }}>
        {/* Sidebar */}
        <div
          className="w-[136px] flex-shrink-0 py-3 px-2.5"
          style={{ borderRight: '1px solid rgba(255,255,255,0.05)' }}
        >
          <div className="flex items-center gap-2 px-2 py-1.5 mb-4">
            <div
              className="w-5 h-5 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: '#1DDFCA' }}
            >
              <span style={{ color: '#fff', fontSize: 8, fontWeight: 700 }}>R</span>
            </div>
            <div>
              <div className="h-2 w-10 rounded" style={{ background: '#1D2A3E' }} />
              <div className="h-1.5 w-7 rounded mt-0.5" style={{ background: '#131D2E' }} />
            </div>
          </div>
          {[
            { label: 'Dashboard', active: false },
            { label: 'Drive', active: false },
            { label: 'Create', active: true },
            { label: 'Publish', active: false },
            { label: 'Settings', active: false },
          ].map((item) => (
            <div
              key={item.label}
              className="flex items-center gap-2 px-2 py-1.5 rounded-lg mb-0.5"
              style={{ background: item.active ? 'rgba(46,121,255,0.12)' : 'transparent' }}
            >
              <div
                className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                style={{ background: item.active ? '#1DDFCA' : '#0C0C1A' }}
              />
              <span
                style={{
                  fontSize: 10,
                  color: item.active ? '#7EB8FF' : '#374F6B',
                  fontWeight: item.active ? 600 : 400,
                }}
              >
                {item.label}
              </span>
            </div>
          ))}
          <div className="mt-5 px-2">
            <div
              style={{
                fontSize: 7.5,
                color: '#1E2E40',
                fontWeight: 700,
                letterSpacing: '0.1em',
                marginBottom: 6,
              }}
            >
              RECENT
            </div>
            {['podcast_ep12.mp4', 'tutorial_v2.mp4'].map((v) => (
              <div key={v} className="flex items-center gap-1.5 py-1">
                <div
                  className="w-2 h-2 rounded-sm flex-shrink-0"
                  style={{ background: '#182030' }}
                />
                <span
                  style={{ fontSize: 8, color: '#283A50' }}
                  className="truncate"
                >
                  {v}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 p-3.5 overflow-hidden flex flex-col min-w-0">
          {/* Tab bar */}
          <div className="flex gap-1.5 mb-3.5 flex-shrink-0">
            {['Smart Detect', 'Talking Camera', 'Reels Creator'].map((tab, i) => (
              <div
                key={tab}
                className="px-2.5 py-1 rounded-lg"
                style={{
                  background:
                    i === 0 ? 'rgba(46,121,255,0.12)' : 'rgba(255,255,255,0.03)',
                  fontSize: 9,
                  color: i === 0 ? '#7EB8FF' : '#3A506A',
                  fontWeight: i === 0 ? 600 : 400,
                  border: `1px solid ${
                    i === 0 ? 'rgba(46,121,255,0.22)' : 'transparent'
                  }`,
                }}
              >
                {tab}
              </div>
            ))}
          </div>

          {/* Selected video bar */}
          <div
            className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl mb-3 flex-shrink-0"
            style={{
              background: 'rgba(46,121,255,0.06)',
              border: '1px solid rgba(46,121,255,0.14)',
            }}
          >
            <div
              className="w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center"
              style={{ background: 'hsl(220,25%,11%)' }}
            >
              <div
                style={{
                  width: 0,
                  height: 0,
                  borderTop: '3px solid transparent',
                  borderBottom: '3px solid transparent',
                  borderLeft: '5px solid rgba(255,255,255,0.4)',
                  marginLeft: 1,
                }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <div
                style={{ fontSize: 9, color: '#C4D0E0', fontWeight: 600 }}
                className="truncate"
              >
                interview_marathon.mp4
              </div>
              <div style={{ fontSize: 8, color: '#3A506A' }}>24:15 duration · 1.8 GB</div>
            </div>
            <div
              className="px-2 py-0.5 rounded-full flex-shrink-0"
              style={{
                background: 'rgba(46,121,255,0.15)',
                fontSize: 8,
                color: '#7EB8FF',
                fontWeight: 600,
              }}
            >
              3 clips
            </div>
          </div>

          {/* Mini waveform */}
          <div className="px-1 mb-3 flex-shrink-0">
            <div className="flex items-end gap-px" style={{ height: 28 }}>
              {miniBars.map((bar, i) => (
                <div
                  key={i}
                  className="flex-1"
                  style={{
                    height: `${bar.h}%`,
                    minHeight: 2,
                    borderRadius: 1.5,
                    background: bar.inClip
                      ? 'linear-gradient(to top, #1DDFCA, #B57EFF)'
                      : 'rgba(255,255,255,0.06)',
                    boxShadow: bar.inClip ? '0 0 4px rgba(46,121,255,0.3)' : 'none',
                  }}
                />
              ))}
            </div>
          </div>

          {/* Clip rows */}
          <div className="space-y-2 flex-1 overflow-hidden">
            {clips.map((clip, i) => (
              <div
                key={i}
                className="flex items-center gap-2.5 py-2 px-3 rounded-xl"
                style={{
                  background: clip.active
                    ? 'rgba(46,121,255,0.08)'
                    : 'rgba(255,255,255,0.025)',
                  border: `1px solid ${
                    clip.active
                      ? 'rgba(46,121,255,0.22)'
                      : 'rgba(255,255,255,0.04)'
                  }`,
                }}
              >
                <div
                  className="w-7 h-7 rounded-lg flex-shrink-0 flex items-center justify-center"
                  style={{ background: `hsl(220,22%,${10 + i * 2}%)` }}
                >
                  <div
                    style={{
                      width: 0,
                      height: 0,
                      borderTop: '3px solid transparent',
                      borderBottom: '3px solid transparent',
                      borderLeft: '4px solid rgba(255,255,255,0.32)',
                      marginLeft: 1,
                    }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div
                    style={{
                      fontSize: 9,
                      color: clip.active ? '#C4D0E0' : '#5C6880',
                      fontWeight: 600,
                    }}
                  >
                    {clip.label}
                  </div>
                  <div style={{ fontSize: 8, color: '#2E4060' }}>{clip.time}</div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {clip.signals.map((s) => (
                    <span
                      key={s}
                      style={{
                        fontSize: 7,
                        color: clip.active ? '#6AA8FF' : '#2E4060',
                        background: clip.active
                          ? 'rgba(46,121,255,0.1)'
                          : 'rgba(255,255,255,0.03)',
                        padding: '1px 4px',
                        borderRadius: 3,
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {s}
                    </span>
                  ))}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: clip.active ? '#1DDFCA' : '#181826',
                    fontWeight: 700,
                    flexShrink: 0,
                    minWidth: 20,
                    textAlign: 'right',
                  }}
                >
                  {clip.score}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: 9:16 preview panel */}
        <div
          className="w-[88px] flex-shrink-0 p-3 flex flex-col"
          style={{ borderLeft: '1px solid rgba(255,255,255,0.05)' }}
        >
          <div
            style={{
              fontSize: 7.5,
              color: '#1E2E40',
              fontWeight: 700,
              letterSpacing: '0.1em',
              marginBottom: 8,
              textTransform: 'uppercase',
            }}
          >
            Preview
          </div>
          <div
            className="rounded-xl overflow-hidden relative mb-3"
            style={{
              background: 'hsl(220,25%,9%)',
              border: '1px solid rgba(46,121,255,0.18)',
              flex: 1,
              minHeight: 0,
            }}
          >
            {/* Score badge */}
            <div
              className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded-md"
              style={{
                background: 'rgba(46,121,255,0.88)',
                fontSize: 7,
                color: '#fff',
                fontWeight: 700,
              }}
            >
              94
            </div>
            {/* Subtitle bar */}
            <div
              className="absolute bottom-2 left-1 right-1 text-center py-0.5 rounded"
              style={{
                background: 'rgba(0,0,0,0.82)',
                fontSize: 6,
                color: '#E4EAF5',
                lineHeight: 1.4,
              }}
            >
              &ldquo;The key insight here is...&rdquo;
            </div>
          </div>
          <div
            className="w-full py-1.5 rounded-lg text-center"
            style={{ background: '#1DDFCA', fontSize: 8, color: '#fff', fontWeight: 600 }}
          >
            Extract
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Pricing Card ─────────────────────────────────────────────────────── */
function PricingCard({ plan, index }) {
  const { name, price, period, features, cta, ctaLink, popular } = plan;

  return (
    <motion.div
      initial={{ opacity: 0, y: 44 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1], delay: index * 0.1 }}
      className="relative rounded-2xl p-8 flex flex-col"
      style={{
        background: popular ? 'rgba(46,121,255,0.07)' : 'rgba(255,255,255,0.03)',
        border: popular
          ? '1px solid rgba(46,121,255,0.38)'
          : '1px solid rgba(255,255,255,0.07)',
        boxShadow: popular
          ? '0 0 64px rgba(46,121,255,0.13), 0 0 0 1px rgba(46,121,255,0.08) inset'
          : 'none',
        transition: 'border-color 0.3s, box-shadow 0.3s',
      }}
      onMouseEnter={(e) => {
        if (!popular) {
          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.13)';
          e.currentTarget.style.boxShadow = '0 8px 36px rgba(0,0,0,0.35)';
        }
      }}
      onMouseLeave={(e) => {
        if (!popular) {
          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)';
          e.currentTarget.style.boxShadow = 'none';
        }
      }}
    >
      {/* Most Popular badge */}
      {popular && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <div
            className="px-4 py-1 rounded-full text-xs font-semibold"
            style={{
              background: '#1DDFCA',
              color: '#fff',
              boxShadow: '0 0 18px rgba(46,121,255,0.55)',
              letterSpacing: '0.06em',
            }}
          >
            MOST POPULAR
          </div>
        </div>
      )}

      {/* Plan header */}
      <div className="mb-7">
        <div
          className="text-xs font-semibold uppercase tracking-widest mb-4"
          style={{ color: popular ? '#7EB8FF' : '#5C6880' }}
        >
          {name}
        </div>
        <div className="flex items-baseline gap-1.5">
          <span
            className="font-bold"
            style={{
              fontSize: 44,
              letterSpacing: '-0.04em',
              color: '#E4EAF5',
              lineHeight: 1,
            }}
          >
            {price}
          </span>
          {period && (
            <span className="text-sm" style={{ color: '#5C6880' }}>
              {period}
            </span>
          )}
        </div>
      </div>

      {/* Feature list */}
      <div className="flex-1 space-y-3.5 mb-8">
        {features.map((f, i) => (
          <div key={i} className="flex items-start gap-3">
            <CheckCircle2
              size={14}
              className="flex-shrink-0 mt-0.5"
              style={{ color: popular ? '#1DDFCA' : '#2A2A42' }}
            />
            <span
              className="text-sm leading-snug"
              style={{ color: popular ? '#8E9AB0' : '#5C6880' }}
            >
              {f}
            </span>
          </div>
        ))}
      </div>

      {/* CTA */}
      {ctaLink ? (
        <Link
          to={ctaLink}
          className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl font-semibold text-sm"
          data-testid={`pricing-${name.toLowerCase().replace(/\s+/g, '-')}-cta`}
          style={{
            background: popular ? '#1DDFCA' : 'rgba(255,255,255,0.06)',
            color: popular ? '#fff' : '#8E9AB0',
            border: popular ? 'none' : '1px solid rgba(255,255,255,0.1)',
            boxShadow: popular ? '0 0 28px rgba(46,121,255,0.32)' : 'none',
            transition: 'all 0.22s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '0.88';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '1';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          {cta} <ArrowRight size={14} />
        </Link>
      ) : (
        <button
          className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl font-semibold text-sm"
          data-testid={`pricing-${name.toLowerCase().replace(/\s+/g, '-')}-cta`}
          style={{
            background: 'rgba(255,255,255,0.04)',
            color: '#5C6880',
            border: '1px solid rgba(255,255,255,0.09)',
            transition: 'all 0.22s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
            e.currentTarget.style.color = '#E4EAF5';
            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.16)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
            e.currentTarget.style.color = '#5C6880';
            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.09)';
          }}
        >
          {cta} <ArrowRight size={14} />
        </button>
      )}
    </motion.div>
  );
}

/* ─── Signal Visual (Hexagonal radar — AI detection) ────────────────────── */
function SignalVisual() {
  return (
    <div className="relative w-full" style={{ maxWidth: 440, margin: '0 auto' }}>
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 55% 55% at 50% 50%, rgba(46,121,255,0.12), transparent)',
          filter: 'blur(28px)',
        }}
      />
      <svg
        viewBox="0 0 400 400"
        className="w-full h-full relative"
        style={{ overflow: 'visible' }}
      >
        {/* Outer atmosphere ring */}
        <circle cx="200" cy="200" r="178" fill="none" stroke="rgba(46,121,255,0.05)" strokeWidth="1">
          <animate attributeName="opacity" values="0.4;0.9;0.4" dur="5s" repeatCount="indefinite" />
        </circle>

        {/* Outer dashed ring — slow rotation */}
        <circle cx="200" cy="200" r="160" fill="none" stroke="rgba(30,205,227,0.09)" strokeWidth="1" strokeDasharray="12 7">
          <animateTransform attributeName="transform" type="rotate" from="0 200 200" to="360 200 200" dur="28s" repeatCount="indefinite" />
        </circle>

        {/* Mid ring — counter-rotation */}
        <circle cx="200" cy="200" r="122" fill="none" stroke="rgba(46,121,255,0.13)" strokeWidth="1" strokeDasharray="6 5">
          <animateTransform attributeName="transform" type="rotate" from="360 200 200" to="0 200 200" dur="20s" repeatCount="indefinite" />
        </circle>

        {/* Inner ring — static */}
        <circle cx="200" cy="200" r="84" fill="none" stroke="rgba(30,205,227,0.07)" strokeWidth="1" />

        {/* Central hexagon */}
        <polygon
          points="200,150 244,175 244,225 200,250 156,225 156,175"
          fill="rgba(46,121,255,0.06)"
          stroke="rgba(46,121,255,0.32)"
          strokeWidth="1.5"
        >
          <animate attributeName="opacity" values="0.65;1;0.65" dur="3.2s" repeatCount="indefinite" />
        </polygon>

        {/* Inner hexagon detail */}
        <polygon
          points="200,170 224,183 224,209 200,222 176,209 176,183"
          fill="none"
          stroke="rgba(30,205,227,0.17)"
          strokeWidth="1"
        />

        {/* Scanning beam */}
        <line x1="200" y1="200" x2="200" y2="40" stroke="rgba(46,121,255,0.28)" strokeWidth="1.5" style={{ filter: 'blur(0.5px)' }}>
          <animateTransform attributeName="transform" type="rotate" from="0 200 200" to="360 200 200" dur="5s" repeatCount="indefinite" />
        </line>

        {/* Scan sweep fill */}
        <path d="M 200 200 L 200 40 A 160 160 0 0 1 249 59 Z" fill="rgba(46,121,255,0.04)">
          <animateTransform attributeName="transform" type="rotate" from="0 200 200" to="360 200 200" dur="5s" repeatCount="indefinite" />
        </path>

        {/* Center core */}
        <circle cx="200" cy="200" r="11" fill="rgba(46,121,255,0.14)" stroke="rgba(46,121,255,0.55)" strokeWidth="1.5">
          <animate attributeName="r" values="10;12;10" dur="2.2s" repeatCount="indefinite" />
        </circle>
        <circle cx="200" cy="200" r="4.5" fill="#1DDFCA" style={{ filter: 'drop-shadow(0 0 7px rgba(29,223,202,0.9))' }} />

        {/* 5 data nodes on mid ring */}
        {[0, 72, 144, 216, 288].map((angle, i) => {
          const rad = ((angle - 90) * Math.PI) / 180;
          const x = 200 + 122 * Math.cos(rad);
          const y = 200 + 122 * Math.sin(rad);
          return (
            <g key={i}>
              <circle cx={x} cy={y} r="5.5" fill="rgba(30,205,227,0.55)"
                style={{ filter: 'drop-shadow(0 0 4px rgba(30,205,227,0.55))' }}>
                <animate attributeName="opacity" values="0.45;1;0.45" dur={`${2.4 + i * 0.45}s`} repeatCount="indefinite" begin={`${i * 0.5}s`} />
              </circle>
              <circle cx={x} cy={y} r="10" fill="none" stroke="rgba(30,205,227,0.18)" strokeWidth="1" />
            </g>
          );
        })}

        {/* Outer node labels */}
        <text x="200" y="24" textAnchor="middle" style={{ fontFamily: 'monospace', fontSize: 9, fill: 'rgba(126,184,255,0.55)', letterSpacing: '0.12em' }}>VIRAL · 94%</text>
        <text x="378" y="146" textAnchor="end" style={{ fontFamily: 'monospace', fontSize: 9, fill: 'rgba(126,184,255,0.5)', letterSpacing: '0.1em' }}>HOOK</text>
        <text x="354" y="302" textAnchor="end" style={{ fontFamily: 'monospace', fontSize: 9, fill: 'rgba(30,205,227,0.55)', letterSpacing: '0.1em' }}>PEAK</text>
        <text x="46" y="302" textAnchor="start" style={{ fontFamily: 'monospace', fontSize: 9, fill: 'rgba(30,205,227,0.55)', letterSpacing: '0.1em' }}>SIGNAL</text>
        <text x="22" y="146" textAnchor="start" style={{ fontFamily: 'monospace', fontSize: 9, fill: 'rgba(126,184,255,0.5)', letterSpacing: '0.1em' }}>DEPTH</text>
      </svg>
    </div>
  );
}

/* ─── Pipeline Visual (Orbital content flow) ────────────────────────────── */
function PipelineVisual() {
  return (
    <div className="relative w-full" style={{ maxWidth: 440, margin: '0 auto' }}>
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 55% 55% at 50% 50%, rgba(139,92,246,0.1), transparent)',
          filter: 'blur(28px)',
        }}
      />
      <svg
        viewBox="0 0 400 400"
        className="w-full h-full relative"
        style={{ overflow: 'visible' }}
      >
        {/* Outer glow ring */}
        <circle cx="200" cy="200" r="178" fill="none" stroke="rgba(139,92,246,0.05)" strokeWidth="1">
          <animate attributeName="opacity" values="0.3;0.7;0.3" dur="6s" repeatCount="indefinite" />
        </circle>

        {/* Orbit 1 — outer tilted ellipse (Publish) */}
        <ellipse cx="200" cy="200" rx="152" ry="58" fill="none" stroke="rgba(139,92,246,0.14)" strokeWidth="1" transform="rotate(-22 200 200)" />

        {/* Orbit 1 — traveling node */}
        <g transform="rotate(-22 200 200)">
          <circle cx="352" cy="200" r="7" fill="#8B5CF6" style={{ filter: 'drop-shadow(0 0 6px rgba(139,92,246,0.7))' }}>
            <animateTransform attributeName="transform" type="rotate" from="0 200 200" to="360 200 200" dur="9s" repeatCount="indefinite" />
          </circle>
        </g>

        {/* Orbit 2 — mid tilted ellipse (Create) */}
        <ellipse cx="200" cy="200" rx="108" ry="50" fill="none" stroke="rgba(46,121,255,0.16)" strokeWidth="1" transform="rotate(14 200 200)" />

        {/* Orbit 2 — traveling node */}
        <g transform="rotate(14 200 200)">
          <circle cx="308" cy="200" r="6" fill="#1DDFCA" style={{ filter: 'drop-shadow(0 0 5px rgba(29,223,202,0.7))' }}>
            <animateTransform attributeName="transform" type="rotate" from="0 200 200" to="360 200 200" dur="6.5s" repeatCount="indefinite" />
          </circle>
        </g>

        {/* Orbit 3 — inner ellipse (Analyse) */}
        <ellipse cx="200" cy="200" rx="68" ry="62" fill="none" stroke="rgba(30,205,227,0.13)" strokeWidth="1" transform="rotate(-5 200 200)" />

        {/* Orbit 3 — traveling node */}
        <g transform="rotate(-5 200 200)">
          <circle cx="268" cy="200" r="5" fill="#B57EFF" style={{ filter: 'drop-shadow(0 0 5px rgba(181,126,255,0.7))' }}>
            <animateTransform attributeName="transform" type="rotate" from="360 200 200" to="0 200 200" dur="4.5s" repeatCount="indefinite" />
          </circle>
        </g>

        {/* Central platform core */}
        <circle cx="200" cy="200" r="36" fill="rgba(139,92,246,0.06)" stroke="rgba(139,92,246,0.3)" strokeWidth="1.5">
          <animate attributeName="opacity" values="0.75;1;0.75" dur="3.5s" repeatCount="indefinite" />
        </circle>
        <circle cx="200" cy="200" r="20" fill="rgba(46,121,255,0.07)" stroke="rgba(46,121,255,0.28)" strokeWidth="1" />

        {/* Radial spokes from core */}
        {[0, 60, 120, 180, 240, 300].map((angle, i) => {
          const rad = ((angle - 90) * Math.PI) / 180;
          const x1 = 200 + 36 * Math.cos(rad);
          const y1 = 200 + 36 * Math.sin(rad);
          const x2 = 200 + 52 * Math.cos(rad);
          const y2 = 200 + 52 * Math.sin(rad);
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(139,92,246,0.2)" strokeWidth="1" />;
        })}

        {/* Center dot */}
        <circle cx="200" cy="200" r="5.5" fill="#8B5CF6" style={{ filter: 'drop-shadow(0 0 9px rgba(139,92,246,0.8))' }} />

        {/* Labels */}
        <text x="200" y="26" textAnchor="middle" style={{ fontFamily: 'monospace', fontSize: 9, fill: 'rgba(139,92,246,0.6)', letterSpacing: '0.12em' }}>PUBLISH</text>
        <text x="376" y="210" textAnchor="end" style={{ fontFamily: 'monospace', fontSize: 9, fill: 'rgba(46,121,255,0.6)', letterSpacing: '0.12em' }}>CREATE</text>
        <text x="36" y="284" textAnchor="start" style={{ fontFamily: 'monospace', fontSize: 9, fill: 'rgba(30,205,227,0.6)', letterSpacing: '0.12em' }}>ANALYSE</text>
      </svg>
    </div>
  );
}

/* ─── Main Landing Page ────────────────────────────────────────────────── */
export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { scrollY } = useScroll();
  const heroMockupY = useTransform(scrollY, [0, 600], [0, -28]);

  const plans = [
    {
      name: 'Free Trial',
      price: 'Free',
      period: '7-day trial',
      popular: false,
      features: [
        'Full platform access during trial',
        'Watermark on exported videos',
        'Limited exports',
        'No credit card required',
      ],
      cta: 'Start free trial',
      ctaLink: '/onboarding',
    },
    {
      name: 'Creator',
      price: '$49',
      period: '/ month',
      popular: true,
      features: [
        'Unlimited exports',
        'No watermark',
        'AI clip detection',
        'AI subtitle generation',
        'Reels creation tools',
        'Content drive',
        'Social publishing',
      ],
      cta: 'Upgrade to Creator',
      ctaLink: null,
    },
    {
      name: 'Agency',
      price: '$199',
      period: '/ month',
      popular: false,
      features: [
        'Everything in Creator',
        'Multiple creators and brands',
        'Team collaboration',
        'Higher processing limits',
        'Advanced analytics',
        'Priority support',
      ],
      cta: 'Contact sales',
      ctaLink: null,
    },
  ];

  const platforms = [
    { name: 'YouTube', color: '#FF0033', bg: 'rgba(255,0,51,0.08)', border: 'rgba(255,0,51,0.2)' },
    { name: 'TikTok', color: '#fff', bg: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.1)' },
    { name: 'Instagram', color: '#E1306C', bg: 'rgba(225,48,108,0.08)', border: 'rgba(225,48,108,0.2)' },
    { name: 'LinkedIn', color: '#0A66C2', bg: 'rgba(10,102,194,0.08)', border: 'rgba(10,102,194,0.2)' },
    { name: 'X', color: '#fff', bg: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.1)' },
    { name: 'Threads', color: '#fff', bg: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.1)' },
  ];

  const oldTools = [
    'Adobe Premiere Pro',
    'CapCut / Descript',
    'Canva / Figma',
    'Buffer / Hootsuite',
    'Notion / Airtable',
  ];

  const navLinks = [
    { label: 'Product', id: 'product' },
    { label: 'Features', id: 'features' },
    { label: 'Pricing', id: 'pricing' },
  ];

  return (
    <div
      style={{
        background: '#090C11',
        minHeight: '100vh',
        fontFamily: 'Inter, sans-serif',
        color: '#E4EAF5',
        overflowX: 'hidden',
      }}
    >
      {/* ── Global CSS keyframes ────────────────────────────────────────── */}
      <style>{`
        @keyframes blob1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(45px, -35px) scale(1.09); }
          66% { transform: translate(-28px, 22px) scale(0.95); }
        }
        @keyframes blob2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          40% { transform: translate(-38px, 32px) scale(1.07); }
          70% { transform: translate(30px, -20px) scale(0.96); }
        }
        @keyframes blob3 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          35% { transform: translate(22px, 40px) scale(1.05); }
          68% { transform: translate(-20px, -28px) scale(0.97); }
        }
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>

      {/* ── Animated background atmosphere ─────────────────────────────── */}
      <div
        className="fixed inset-0 overflow-hidden pointer-events-none"
        style={{ zIndex: 0 }}
      >
        <div
          style={{
            position: 'absolute', width: 720, height: 720,
            top: -200, left: -180,
            background: 'radial-gradient(ellipse at center, rgba(46,121,255,0.11) 0%, rgba(46,121,255,0.04) 45%, transparent 70%)',
            animation: 'blob1 22s ease-in-out infinite',
            willChange: 'transform',
          }}
        />
        <div
          style={{
            position: 'absolute', width: 560, height: 560,
            top: '28%', right: -120,
            background: 'radial-gradient(ellipse at center, rgba(30,205,227,0.07) 0%, rgba(30,205,227,0.025) 45%, transparent 70%)',
            animation: 'blob2 28s ease-in-out infinite 6s',
            willChange: 'transform',
          }}
        />
        <div
          style={{
            position: 'absolute', width: 900, height: 450,
            bottom: '8%', left: '5%',
            background: 'radial-gradient(ellipse at center, rgba(139,92,246,0.06) 0%, rgba(139,92,246,0.02) 45%, transparent 70%)',
            animation: 'blob3 34s ease-in-out infinite 12s',
            willChange: 'transform',
          }}
        />
      </div>

      {/* ── Navbar ─────────────────────────────────────────────────────── */}
      <header
        className="fixed top-0 left-0 right-0 z-50"
        style={{
          background: 'rgba(9,12,17,0.88)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
        }}
      >
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center gap-8">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center gap-2.5 flex-shrink-0 bg-transparent border-0 cursor-pointer p-0"
          >
            <LogoC size={30} />
            <span
              className="font-semibold text-base"
              style={{ letterSpacing: '-0.02em', color: '#E4EAF5' }}
            >
              Reelia
            </span>
          </button>

          <nav className="hidden md:flex items-center gap-7 flex-1">
            {navLinks.map(({ label, id }) => (
              <button
                key={label}
                onClick={() => scrollTo(id)}
                className="text-sm bg-transparent border-0 cursor-pointer p-0"
                style={{ color: '#5C6880', transition: 'color 0.2s', fontFamily: 'inherit' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#E4EAF5')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#5C6880')}
              >
                {label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3 ml-auto">
            <Link
              to="/login"
              className="hidden sm:block text-sm px-4 py-2 rounded-lg"
              style={{ color: '#8E9AB0', transition: 'color 0.2s' }}
              data-testid="nav-signin-btn"
              onMouseEnter={(e) => (e.currentTarget.style.color = '#E4EAF5')}
              onMouseLeave={(e) => (e.currentTarget.style.color = '#8E9AB0')}
            >
              Login
            </Link>
            <Link
              to="/onboarding"
              className="text-sm px-4 py-2 rounded-lg font-medium"
              style={{
                background: '#1DDFCA',
                color: '#fff',
                boxShadow: '0 0 22px rgba(46,121,255,0.3)',
                transition: 'all 0.22s',
                letterSpacing: '-0.01em',
              }}
              data-testid="nav-trial-btn"
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '0.88';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '1';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Start free trial
            </Link>
            <button
              className="md:hidden p-2 rounded-lg bg-transparent border-0 cursor-pointer"
              style={{ background: 'rgba(255,255,255,0.05)' }}
              onClick={() => setMobileMenuOpen((v) => !v)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? (
                <X size={18} style={{ color: '#8E9AB0' }} />
              ) : (
                <Menu size={18} style={{ color: '#8E9AB0' }} />
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18 }}
            className="md:hidden px-6 pb-4 pt-2"
            style={{
              borderTop: '1px solid rgba(255,255,255,0.06)',
              background: 'rgba(9,12,17,0.97)',
            }}
          >
            {navLinks.map(({ label, id }) => (
              <button
                key={label}
                onClick={() => {
                  scrollTo(id);
                  setMobileMenuOpen(false);
                }}
                className="block w-full text-left py-3 text-sm bg-transparent border-0 cursor-pointer"
                style={{
                  color: '#8E9AB0',
                  fontFamily: 'inherit',
                  borderBottom: '1px solid rgba(255,255,255,0.04)',
                }}
              >
                {label}
              </button>
            ))}
            <Link
              to="/login"
              onClick={() => setMobileMenuOpen(false)}
              className="block py-3 text-sm"
              style={{ color: '#8E9AB0' }}
            >
              Login
            </Link>
          </motion.div>
        )}
      </header>

      {/* ── Hero ───────────────────────────────────────────────────────── */}
      <section className="relative pt-20 pb-12 overflow-hidden" style={{ zIndex: 1 }}>
        {/* Radial glow — anchored to left where text lives */}
        <div
          className="absolute top-0 left-0 pointer-events-none"
          style={{
            width: '65%',
            maxWidth: 900,
            height: '110%',
            background:
              'radial-gradient(ellipse 70% 60% at 10% -10%, rgba(46,121,255,0.16), transparent)',
          }}
        />
        {/* Dot grid — fades toward right */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(rgba(46,121,255,0.1) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
            maskImage:
              'radial-gradient(ellipse 75% 55% at 20% 5%, black 10%, transparent 72%)',
            WebkitMaskImage:
              'radial-gradient(ellipse 75% 55% at 20% 5%, black 10%, transparent 72%)',
          }}
        />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-[1fr_1.18fr] gap-10 xl:gap-14 items-center">

            {/* ── LEFT: Copy + CTAs ─────────────────────────────────── */}
            <div className="lg:py-6">
              {/* Badge */}
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-7"
                style={{
                  background: 'rgba(46,121,255,0.08)',
                  border: '1px solid rgba(46,121,255,0.22)',
                }}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full inline-block"
                  style={{ background: '#1DDFCA', animation: 'pulse 2s infinite' }}
                />
                <span
                  className="text-xs font-medium"
                  style={{ color: '#7EB8FF', letterSpacing: '0.04em' }}
                >
                  Introducing Reelia — Content OS
                </span>
              </motion.div>

              {/* Headline */}
              <motion.h1
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.03 }}
                className="font-bold mb-6 leading-tight"
                style={{
                  fontSize: 'clamp(36px, 4.4vw, 68px)',
                  letterSpacing: '-0.038em',
                  color: '#E4EAF5',
                  fontWeight: 800,
                  lineHeight: 1.06,
                }}
              >
                The operating system
                <br />
                for{' '}
                <span
                  style={{
                    color: '#1DDFCA',
                    textShadow: '0 0 52px rgba(46,121,255,0.5)',
                  }}
                >
                  content creators.
                </span>
              </motion.h1>

              {/* Sub-headline */}
              <motion.p
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.07 }}
                className="text-lg leading-relaxed mb-3 font-normal"
                style={{ color: '#8E9AB0', maxWidth: 460, letterSpacing: '-0.01em' }}
              >
                Plan, create, edit and publish viral content from one AI-powered workspace.
              </motion.p>

              <motion.p
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
                className="text-sm leading-relaxed mb-10"
                style={{ color: '#4A5E78', maxWidth: 400 }}
              >
                Designed for creators who want to operate like a full content team.
                Reelia replaces complexity with a single platform.
              </motion.p>

              {/* CTAs */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.13 }}
                className="flex items-center gap-4 flex-wrap"
              >
                <Link
                  to="/onboarding"
                  className="flex items-center gap-2 px-7 py-4 rounded-xl font-semibold text-sm"
                  style={{
                    background: '#1DDFCA',
                    color: '#fff',
                    boxShadow: '0 0 36px rgba(46,121,255,0.4)',
                    transition: 'all 0.25s',
                    letterSpacing: '-0.01em',
                  }}
                  data-testid="hero-trial-btn"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 0 56px rgba(46,121,255,0.58)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 0 36px rgba(46,121,255,0.4)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  Start free trial <ArrowRight size={15} />
                </Link>
                <button
                  className="flex items-center gap-2 px-7 py-4 rounded-xl font-medium text-sm"
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    color: '#8E9AB0',
                    border: '1px solid rgba(255,255,255,0.09)',
                    transition: 'all 0.25s',
                  }}
                  data-testid="hero-demo-btn"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.09)';
                    e.currentTarget.style.color = '#E4EAF5';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.16)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                    e.currentTarget.style.color = '#8E9AB0';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.09)';
                  }}
                >
                  <Play size={13} style={{ fill: 'currentColor' }} /> Watch demo
                </button>
              </motion.div>

              {/* Trust line */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.7, delay: 0.28 }}
                className="mt-7 text-xs"
                style={{ color: '#181826' }}
              >
                7-day free trial — No credit card required.
              </motion.p>
            </div>

            {/* ── RIGHT: Product mockup ──────────────────────────────── */}
            <motion.div
              initial={{ opacity: 0, x: 28 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.08 }}
              style={{
                y: heroMockupY,
                boxShadow:
                  '0 24px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.07), 0 0 56px rgba(46,121,255,0.1)',
                borderRadius: 16,
              }}
            >
              <HeroMockup />
            </motion.div>

          </div>
        </div>
      </section>

      {/* ── Ticker ─────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden" style={{ zIndex: 1, borderTop: '1px solid rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        {/* Edge fades */}
        <div className="absolute inset-y-0 left-0 w-28 z-10 pointer-events-none"
          style={{ background: 'linear-gradient(to right, #090C11, transparent)' }} />
        <div className="absolute inset-y-0 right-0 w-28 z-10 pointer-events-none"
          style={{ background: 'linear-gradient(to left, #090C11, transparent)' }} />
        <div
          className="flex items-center py-5"
          style={{
            animation: 'ticker 36s linear infinite',
            width: 'max-content',
            willChange: 'transform',
          }}
        >
          {[
            'Made by creators',
            'For creators',
            'Built for modern content teams',
            'AI powered creation workflow',
            'From long videos to viral clips',
            'The operating system for content creators',
            'Create faster',
            'Publish everywhere',
            'Made by creators',
            'For creators',
            'Built for modern content teams',
            'AI powered creation workflow',
            'From long videos to viral clips',
            'The operating system for content creators',
            'Create faster',
            'Publish everywhere',
          ].map((item, i) => (
            <span key={i} className="flex items-center">
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: i % 3 === 0 ? '#4A6B9A' : i % 3 === 1 ? '#3D5272' : '#2E4060',
                  letterSpacing: '0.025em',
                  whiteSpace: 'nowrap',
                  padding: '0 28px',
                }}
              >
                {item}
              </span>
              <span
                style={{
                  width: 3,
                  height: 3,
                  borderRadius: '50%',
                  background: i % 2 === 0 ? 'rgba(46,121,255,0.45)' : 'rgba(30,205,227,0.35)',
                  display: 'inline-block',
                  flexShrink: 0,
                }}
              />
            </span>
          ))}
        </div>
      </section>

      {/* ── Section 1: Product Overview ────────────────────────────────── */}
      <section id="product" className="py-36 max-w-6xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-20 items-center">
          <motion.div {...fadeUp()}>
            <SectionLabel>Product Overview — 01</SectionLabel>
            <h2
              className="font-bold mb-7 leading-tight"
              style={{ fontSize: 'clamp(32px, 4.5vw, 54px)', letterSpacing: '-0.035em' }}
            >
              One platform for your entire content workflow.
            </h2>
            <p
              className="text-base leading-relaxed mb-11"
              style={{ color: '#8E9AB0', lineHeight: 1.85 }}
            >
              Upload long-form videos. Detect viral moments. Create clips. Edit and publish
              to every platform — without leaving your workspace.
            </p>
            <div className="space-y-4">
              {[
                'Upload once, repurpose everywhere',
                'Organized Drive with smart folder structure',
                'AI detects the moments that drive engagement',
                'Full editorial control over every clip',
              ].map((item, i) => (
                <motion.div
                  key={item}
                  initial={{ opacity: 0, x: -18 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.55, delay: 0.1 + i * 0.09, ease: [0.16, 1, 0.3, 1] }}
                  className="flex items-center gap-3.5"
                >
                  <div
                    className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{
                      background: 'rgba(46,121,255,0.1)',
                      border: '1px solid rgba(46,121,255,0.25)',
                    }}
                  >
                    <CheckCircle2 size={11} style={{ color: '#1DDFCA' }} />
                  </div>
                  <span className="text-sm" style={{ color: '#8E9AB0' }}>
                    {item}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>
          <motion.div
            {...fadeUp(0.13)}
            style={{
              boxShadow:
                '0 28px 88px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.07)',
              borderRadius: 16,
            }}
          >
            <AppMockup />
          </motion.div>
        </div>
      </section>

      <Divider />

      {/* ── Section 2: AI Engine ───────────────────────────────────────── */}
      <section id="features" className="py-36 max-w-6xl mx-auto px-6">
        <motion.div {...fadeUp()} className="text-center mb-18">
          <SectionLabel>AI Content Engine — 02</SectionLabel>
          <h2
            className="font-bold leading-tight"
            style={{
              fontSize: 'clamp(32px, 4.5vw, 54px)',
              letterSpacing: '-0.035em',
              maxWidth: 660,
              margin: '0 auto 20px',
            }}
          >
            Find your viral moments automatically.
          </h2>
          <p
            className="text-base"
            style={{ color: '#8E9AB0', maxWidth: 480, margin: '0 auto', lineHeight: 1.85 }}
          >
            Reelia's AI scans your entire video and surfaces the exact moments with the
            highest viral potential — scored by engagement signals.
          </p>
        </motion.div>

        <motion.div {...fadeUp(0.1)}>
          <WaveformMockup />
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 mt-8">
          {[
            { stat: 3, suffix: '×', label: 'faster than manual editing', desc: 'Skip hours of scrubbing through footage' },
            { stat: 94, suffix: '%', label: 'detection accuracy', desc: 'Trained on millions of viral content pieces' },
            { stat: null, sym: '∞', label: 'clips from one upload', desc: 'Every viral angle, extracted and ready' },
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.14 + i * 0.11, ease: [0.16, 1, 0.3, 1] }}
              className="rounded-2xl p-7"
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.07)',
                transition: 'border-color 0.3s, box-shadow 0.3s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(46,121,255,0.28)';
                e.currentTarget.style.boxShadow = '0 0 36px rgba(46,121,255,0.07)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div
                className="font-bold mb-2"
                style={{ fontSize: 48, color: '#1DDFCA', letterSpacing: '-0.04em', lineHeight: 1 }}
              >
                {item.sym ? (
                  item.sym
                ) : (
                  <AnimatedCounter value={item.stat} suffix={item.suffix} />
                )}
              </div>
              <div className="text-sm font-semibold mb-2" style={{ color: '#E4EAF5' }}>
                {item.label}
              </div>
              <div className="text-xs leading-relaxed" style={{ color: '#4A5E78' }}>
                {item.desc}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <Divider />

      {/* ── Geometric Section 1: Precision AI Detection ────────────────── */}
      <section className="py-36 max-w-6xl mx-auto px-6">
        <div className="grid lg:grid-cols-[1fr_1fr] gap-16 xl:gap-24 items-center">
          <motion.div {...fadeUp()}>
            <SectionLabel>AI Engine — 02b</SectionLabel>
            <h2
              className="font-bold mb-6 leading-tight"
              style={{ fontSize: 'clamp(32px, 4vw, 52px)', letterSpacing: '-0.035em' }}
            >
              Precision AI.
              <br />
              Real-time signals.
            </h2>
            <p className="text-base leading-relaxed mb-10" style={{ color: '#8E9AB0', lineHeight: 1.85, maxWidth: 420 }}>
              Every second of your footage is scanned for engagement potential. Reelia's engine
              identifies hooks, emotional peaks, and viral signals with frame-level precision.
            </p>
            <div className="space-y-4">
              {[
                { label: 'Engagement scoring per second of footage', color: '#1DDFCA' },
                { label: 'Hook detection trained on millions of viral clips', color: '#B57EFF' },
                { label: 'Sub-second analysis with 94% accuracy', color: '#8B5CF6' },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.55, delay: 0.1 + i * 0.09, ease: [0.16, 1, 0.3, 1] }}
                  className="flex items-center gap-3"
                >
                  <div
                    className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{ background: item.color, boxShadow: `0 0 6px ${item.color}` }}
                  />
                  <span className="text-sm" style={{ color: '#8E9AB0' }}>{item.label}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            {...fadeUp(0.12)}
            className="relative"
          >
            <SignalVisual />
          </motion.div>
        </div>
      </section>

      <Divider />

      {/* ── Section 3: Reels Workflow ───────────────────────────────────── */}
      <section className="py-36 max-w-6xl mx-auto px-6">
        <motion.div {...fadeUp()} className="text-center mb-20">
          <SectionLabel>Reels Workflow — 03</SectionLabel>
          <h2
            className="font-bold"
            style={{ fontSize: 'clamp(32px, 4.5vw, 54px)', letterSpacing: '-0.035em', marginBottom: 18 }}
          >
            Long video to viral reel in minutes.
          </h2>
          <p style={{ color: '#8E9AB0', fontSize: 16 }}>
            Four steps. One platform. Zero friction.
          </p>
        </motion.div>

        <div className="relative grid grid-cols-2 lg:grid-cols-4 gap-5">
          {/* Connector line */}
          <div
            className="hidden lg:block absolute pointer-events-none"
            style={{
              top: 54,
              left: '14%',
              right: '14%',
              height: 1,
              background:
                'linear-gradient(90deg, transparent, rgba(46,121,255,0.28) 20%, rgba(30,205,227,0.28) 80%, transparent)',
            }}
          />

          {[Upload, Sparkles, Scissors, Share2].map((Icon, i) => {
            const labels = ['Upload', 'AI Detection', 'Auto-Cut', 'Publish'];
            const descs = [
              'Drop your long-form video',
              'Viral moments identified',
              'Portrait-ready & captioned',
              'Everywhere, automatically',
            ];
            const nums = ['01', '02', '03', '04'];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                className="relative rounded-2xl p-7 text-center"
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  transition: 'border-color 0.22s, background 0.22s, box-shadow 0.22s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(46,121,255,0.38)';
                  e.currentTarget.style.background = 'rgba(46,121,255,0.05)';
                  e.currentTarget.style.boxShadow = '0 0 36px rgba(46,121,255,0.09)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)';
                  e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div
                  className="text-xs font-bold mb-5"
                  style={{ color: 'rgba(46,121,255,0.5)', letterSpacing: '0.13em' }}
                >
                  {nums[i]}
                </div>
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-5"
                  style={{
                    background: 'rgba(46,121,255,0.1)',
                    border: '1px solid rgba(46,121,255,0.22)',
                  }}
                >
                  <Icon size={20} style={{ color: '#1DDFCA' }} />
                </div>
                <div
                  className="font-semibold mb-2 text-sm"
                  style={{ color: '#E4EAF5', letterSpacing: '-0.01em' }}
                >
                  {labels[i]}
                </div>
                <div className="text-xs leading-relaxed" style={{ color: '#4A5E78' }}>
                  {descs[i]}
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      <Divider />

      {/* ── Geometric Section 2: Content Pipeline ──────────────────────── */}
      <section className="py-36 max-w-6xl mx-auto px-6">
        <div className="grid lg:grid-cols-[1fr_1fr] gap-16 xl:gap-24 items-center">
          <motion.div {...fadeUp()} className="order-2 lg:order-1 relative">
            <PipelineVisual />
          </motion.div>

          <motion.div {...fadeUp(0.1)} className="order-1 lg:order-2">
            <SectionLabel>Content Pipeline — 03b</SectionLabel>
            <h2
              className="font-bold mb-6 leading-tight"
              style={{ fontSize: 'clamp(32px, 4vw, 52px)', letterSpacing: '-0.035em' }}
            >
              One pipeline.
              <br />
              Every step.
            </h2>
            <p className="text-base leading-relaxed mb-10" style={{ color: '#8E9AB0', lineHeight: 1.85, maxWidth: 420 }}>
              From raw footage to published reel — Reelia orchestrates the entire workflow.
              No context switching, no lost files, no wasted time between tools.
            </p>
            <div className="space-y-4">
              {[
                { label: 'Analyse your footage for viral potential', color: '#B57EFF' },
                { label: 'Create and refine in one workspace', color: '#1DDFCA' },
                { label: 'Publish to every platform automatically', color: '#8B5CF6' },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.55, delay: 0.1 + i * 0.09, ease: [0.16, 1, 0.3, 1] }}
                  className="flex items-center gap-3"
                >
                  <div
                    className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{ background: item.color, boxShadow: `0 0 6px ${item.color}` }}
                  />
                  <span className="text-sm" style={{ color: '#8E9AB0' }}>{item.label}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <Divider />

      {/* ── Section 4: Publishing ──────────────────────────────────────── */}
      <section className="py-36 max-w-6xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-20 items-center">
          <motion.div {...fadeUp()} className="grid grid-cols-3 gap-3 order-2 lg:order-1">
            {platforms.map((p, i) => (
              <motion.div
                key={p.name}
                initial={{ opacity: 0, y: 26, scale: 0.96 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: i * 0.07, ease: [0.16, 1, 0.3, 1] }}
                className="rounded-xl p-5 flex flex-col items-center gap-3"
                style={{
                  background: p.bg,
                  border: `1px solid ${p.border}`,
                  transition: 'transform 0.25s, box-shadow 0.25s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = `0 10px 28px ${p.border}`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div className="text-sm font-bold" style={{ color: p.color, letterSpacing: '-0.01em' }}>
                  {p.name}
                </div>
                <div
                  className="text-xs px-2 py-0.5 rounded-full"
                  style={{ background: 'rgba(255,255,255,0.05)', color: '#5C6880', fontSize: 9 }}
                >
                  Connected
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div {...fadeUp(0.1)} className="order-1 lg:order-2">
            <SectionLabel>Publishing — 04</SectionLabel>
            <h2
              className="font-bold mb-7 leading-tight"
              style={{ fontSize: 'clamp(32px, 4.5vw, 52px)', letterSpacing: '-0.035em' }}
            >
              Publish everywhere.
              <br />
              Manage from one place.
            </h2>
            <p className="text-base leading-relaxed" style={{ color: '#8E9AB0', lineHeight: 1.85 }}>
              Connect your social accounts and schedule content directly from Reelia. Post to
              YouTube, TikTok, Instagram, LinkedIn and more — all from a single publishing
              dashboard.
            </p>
          </motion.div>
        </div>
      </section>

      <Divider />

      {/* ── Section 5: Speed Comparison ────────────────────────────────── */}
      <section className="py-36 max-w-6xl mx-auto px-6">
        <motion.div {...fadeUp()} className="text-center mb-20">
          <SectionLabel>Speed & Efficiency — 05</SectionLabel>
          <h2
            className="font-bold"
            style={{ fontSize: 'clamp(32px, 4.5vw, 54px)', letterSpacing: '-0.035em', marginBottom: 18 }}
          >
            Stop juggling five tools.
          </h2>
          <p style={{ color: '#8E9AB0', fontSize: 16 }}>
            Replace your entire content stack with one platform.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Without Reelia */}
          <motion.div
            {...fadeUp(0.05)}
            className="rounded-2xl p-9"
            style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
          >
            <div
              className="text-xs font-semibold uppercase tracking-widest mb-7"
              style={{ color: '#3D4D62' }}
            >
              Without Reelia
            </div>
            <div className="space-y-3">
              {oldTools.map((tool) => (
                <div
                  key={tool}
                  className="flex items-center gap-3 py-3 px-4 rounded-xl"
                  style={{
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.04)',
                  }}
                >
                  <div
                    className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{ background: '#181826' }}
                  />
                  <span
                    className="text-sm line-through"
                    style={{ color: '#3D4D62', textDecorationColor: 'rgba(255,255,255,0.12)' }}
                  >
                    {tool}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-7 text-xs" style={{ color: '#181826' }}>
              5 subscriptions · $150+/mo · Constant context-switching
            </div>
          </motion.div>

          {/* With Reelia */}
          <motion.div
            {...fadeUp(0.13)}
            className="rounded-2xl p-9 relative overflow-hidden"
            style={{
              background: 'rgba(46,121,255,0.06)',
              border: '1px solid rgba(46,121,255,0.2)',
              boxShadow: '0 0 56px rgba(46,121,255,0.07)',
            }}
          >
            <div
              className="absolute top-0 right-0 w-64 h-64 pointer-events-none"
              style={{
                background: 'radial-gradient(circle at 85% -15%, rgba(46,121,255,0.2), transparent)',
              }}
            />
            <div
              className="text-xs font-semibold uppercase tracking-widest mb-7"
              style={{ color: '#7EB8FF' }}
            >
              With Reelia
            </div>
            <div
              className="rounded-2xl py-4 px-5 mb-5"
              style={{
                background: 'rgba(46,121,255,0.08)',
                border: '1px solid rgba(46,121,255,0.2)',
              }}
            >
              <div className="flex items-center gap-3">
                <LogoC size={36} />
                <div>
                  <div className="text-sm font-semibold" style={{ color: '#E4EAF5' }}>
                    Reelia — Content OS
                  </div>
                  <div className="text-xs" style={{ color: '#7EB8FF' }}>
                    All-in-one workspace
                  </div>
                </div>
                <div
                  className="ml-auto px-2 py-1 rounded-full text-xs font-medium"
                  style={{ background: 'rgba(46,121,255,0.22)', color: '#7EB8FF', fontSize: 10 }}
                >
                  Active
                </div>
              </div>
            </div>
            <div className="space-y-3 mb-7">
              {[
                'Upload & organize with Drive',
                'AI detects viral moments',
                'Build reels with editor',
                'Auto-captions & subtitles',
                'Schedule & publish everywhere',
              ].map((f) => (
                <div key={f} className="flex items-center gap-3">
                  <CheckCircle2 size={13} style={{ color: '#1DDFCA', flexShrink: 0 }} />
                  <span className="text-sm" style={{ color: '#8E9AB0' }}>
                    {f}
                  </span>
                </div>
              ))}
            </div>
            <div className="text-xs" style={{ color: '#4A6B9A' }}>
              1 subscription · 7-day free trial · No credit card
            </div>
          </motion.div>
        </div>
      </section>

      <Divider />

      {/* ── Section 6: Pricing ─────────────────────────────────────────── */}
      <section id="pricing" className="py-36 relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse 65% 50% at 50% 50%, rgba(46,121,255,0.055), transparent)',
          }}
        />
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <motion.div {...fadeUp()} className="text-center mb-16">
            <SectionLabel>Pricing — 06</SectionLabel>
            <h2
              className="font-bold leading-tight"
              style={{
                fontSize: 'clamp(32px, 4.5vw, 54px)',
                letterSpacing: '-0.035em',
                marginBottom: 18,
              }}
            >
              Simple, transparent pricing.
            </h2>
            <p style={{ color: '#8E9AB0', fontSize: 16 }}>
              Start free. Scale when you're ready.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-6 items-start">
            {plans.map((plan, i) => (
              <PricingCard key={plan.name} plan={plan} index={i} />
            ))}
          </div>
        </div>
      </section>

      <Divider />

      {/* ── Final CTA ──────────────────────────────────────────────────── */}
      <section className="py-44 relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse 80% 65% at 50% 50%, rgba(46,121,255,0.1), transparent)',
          }}
        />
        {/* Dot grid */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(rgba(46,121,255,0.1) 1px, transparent 1px)',
            backgroundSize: '44px 44px',
            maskImage: 'radial-gradient(ellipse 55% 55% at 50% 50%, black, transparent)',
            WebkitMaskImage: 'radial-gradient(ellipse 55% 55% at 50% 50%, black, transparent)',
          }}
        />
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <motion.div {...fadeUp()}>
            <SectionLabel>Get Started</SectionLabel>
            <h2
              className="font-bold mb-7 leading-tight"
              style={{ fontSize: 'clamp(42px, 6.5vw, 76px)', letterSpacing: '-0.04em' }}
            >
              Build your
              <br />
              <span
                style={{
                  color: '#1DDFCA',
                  textShadow: '0 0 72px rgba(46,121,255,0.45)',
                }}
              >
                content machine.
              </span>
            </h2>
            <p className="text-lg mb-12" style={{ color: '#8E9AB0', lineHeight: 1.75 }}>
              Join creators and agencies already using Reelia to produce more content, faster.
            </p>
            <Link
              to="/onboarding"
              className="inline-flex items-center gap-2.5 rounded-xl font-semibold text-base"
              style={{
                background: '#1DDFCA',
                color: '#fff',
                boxShadow: '0 0 52px rgba(46,121,255,0.48)',
                transition: 'all 0.25s',
                letterSpacing: '-0.01em',
                padding: '18px 40px',
              }}
              data-testid="cta-trial-btn"
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 0 80px rgba(46,121,255,0.65)';
                e.currentTarget.style.transform = 'translateY(-3px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 0 52px rgba(46,121,255,0.48)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Start free trial <ArrowRight size={16} />
            </Link>
            <p className="mt-6 text-sm" style={{ color: '#3D5272' }}>
              7-day free trial — No credit card required.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────────────────────── */}
      <footer style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <div className="max-w-6xl mx-auto px-6 py-12 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <LogoC size={28} />
            <span
              className="font-semibold text-sm"
              style={{ color: '#E4EAF5', letterSpacing: '-0.01em' }}
            >
              Reelia
            </span>
          </div>
          <div className="flex items-center gap-6">
            {['Privacy', 'Terms', 'Support'].map((link) => (
              <a
                key={link}
                href="#"
                className="text-xs"
                style={{ color: '#3D5272', transition: 'color 0.2s' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#8E9AB0')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#3D5272')}
              >
                {link}
              </a>
            ))}
          </div>
          <p className="text-xs" style={{ color: '#181826' }}>
            © 2026 Reelia. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
